import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { t } from "i18next";
import {
  Button,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Spinner,
  Input,
  Row,
  Col,
  Card,
  CardBody,
} from "reactstrap";
import { useSpacePopupStore } from "../../../../stores/Assets/spacePopup";
import { useSpaceStore } from "../../../../stores/Assets/space";
import { useSiteStore } from "../../../../stores/Assets/site";
import { FormControlLabel, Switch } from "@mui/material";
import Select from "react-select";
import { toast } from "react-toastify";

// Helper function to convert local datetime to UTC ISO string
const localDateTimeToUTC = (localDateTimeString) => {
  if (!localDateTimeString) return undefined;
  // Create a date object from the local datetime string
  // This will be interpreted as local time
  const localDate = new Date(localDateTimeString);
  // Convert to ISO string (UTC)
  return localDate.toISOString();
};

const AddAnnouncement = ({ showAddAnnouncementModal, toggleAddAnnouncementModal }) => {
  const { createSpacePopup, isLoading } = useSpacePopupStore((state) => state);
  const { getSpaces } = useSpaceStore((state) => state);
  const { getSites, sites } = useSiteStore((state) => state);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [previewLanguage, setPreviewLanguage] = useState("EN"); // EN or AR
  const [selectedSiteId, setSelectedSiteId] = useState(null);
  const [allSpaces, setAllSpaces] = useState([]);
  const [selectedSpaceIds, setSelectedSpaceIds] = useState([]);

  // Fetch sites on mount
  useEffect(() => {
    const fetchSites = async () => {
      await getSites();
    };
    fetchSites();
  }, []);

  // Fetch spaces when site is selected
  useEffect(() => {
    const fetchSpaces = async () => {
      if (selectedSiteId) {
        await getSpaces(selectedSiteId, { page: 1, perPage: 500, search: "" });
      }
    };
    fetchSpaces();
  }, [selectedSiteId]);

  // Update allSpaces when spaces change and auto-select all spaces
  const spaces = useSpaceStore((state) => state.spaces);
  useEffect(() => {
    if (spaces && selectedSiteId) {
      setAllSpaces(spaces);
      // Auto-select all spaces when they're loaded (represented by "ALL" value)
      setSelectedSpaceIds(["ALL"]);
    }
  }, [spaces, selectedSiteId]);

  // Check if "All" is selected
  const isAllSelected = selectedSpaceIds.includes("ALL");
  
  // Get actual space IDs (filter out "ALL")
  const actualSelectedSpaceIds = selectedSpaceIds.filter((id) => id !== "ALL");

  const formik = useFormik({
    initialValues: {
      title: "",
      title_ar: "",
      description: "",
      description_ar: "",
      image: "",
      starts_at: "",
      expires_at: "",
      see_once: false,
    },
    validationSchema: Yup.object({
      title: Yup.string().optional(),
      title_ar: Yup.string().optional(),
      description: Yup.string().optional(),
      description_ar: Yup.string().optional(),
      image: Yup.string().optional(),
      starts_at: Yup.string().optional(),
      expires_at: Yup.string().optional(),
      see_once: Yup.boolean().optional(),
    }),
    onSubmit: async (values) => {
      //if theres no spaces selected or (no title/description/image) throw an error
      const actualSelectedIds = selectedSpaceIds.filter((id) => id !== "ALL");
      const hasSpacesSelected = selectedSpaceIds.length > 0 && 
        (selectedSpaceIds.includes("ALL") || actualSelectedIds.length > 0);
      if (!hasSpacesSelected || (!values.title && !values.title_ar && !values.description && !imageFile)) {
        toast.error(t("Please select at least one space and provide a title, description, and image"));
        return;
      }
      const formData = new FormData();
      formData.append("title", values.title);
      if (values.title_ar) formData.append("title_ar", values.title_ar);
      if (values.description) formData.append("description", values.description);
      if (values.description_ar) formData.append("description_ar", values.description_ar);
      if (values.starts_at) formData.append("starts_at", localDateTimeToUTC(values.starts_at));
      if (values.expires_at) formData.append("expires_at", localDateTimeToUTC(values.expires_at));
      formData.append("see_once", values.see_once.toString());
      
      // Add image file if provided
      if (imageFile) {
        formData.append("file", imageFile);
      }

      // Add space IDs as JSON string
      // If "ALL" is selected, convert it to all space IDs
      let spaceIdsToSend = selectedSpaceIds;
      if (selectedSpaceIds.includes("ALL")) {
        spaceIdsToSend = allSpaces.map((space) => space.space_id);
      }
      if (spaceIdsToSend.length > 0) {
        formData.append("spaceIds", JSON.stringify(spaceIdsToSend));
      }

      const result = await createSpacePopup(formData);
      if (result) {
        formik.resetForm();
        setImagePreview(null);
        setImageFile(null);
        setSelectedSpaceIds([]);
        setSelectedSiteId(null);
        toggleAddAnnouncementModal();
      }
    },
  });

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setImagePreview(base64String);
      };
      reader.readAsDataURL(file);
    }
  };

  // Get preview data based on selected language
  const getPreviewData = () => {
    if (previewLanguage === "AR") {
      return {
        title: formik.values.title_ar || formik.values.title,
        description: formik.values.description_ar || formik.values.description,
      };
    }
    return {
      title: formik.values.title,
      description: formik.values.description,
    };
  };

  const previewData = getPreviewData();

  return (
    <Modal isOpen={showAddAnnouncementModal} toggle={toggleAddAnnouncementModal} size="lg">
      <ModalHeader toggle={toggleAddAnnouncementModal}>
        {t("Add New Announcement")}
      </ModalHeader>
      <ModalBody>
        <Row>
          <Col md={6}>
            <form onSubmit={formik.handleSubmit}>
              <div className="mb-3">
                <Label for="title">{t("Title")}</Label>
                <Input
                  type="text"
                  id="title"
                  name="title"
                  value={formik.values.title}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.title && !!formik.errors.title}
                />
                {formik.touched.title && formik.errors.title && (
                  <div className="text-danger small">{formik.errors.title}</div>
                )}
              </div>

              <div className="mb-3">
                <Label for="title_ar">{t("Title (AR)")}</Label>
                <Input
                  type="text"
                  id="title_ar"
                  name="title_ar"
                  value={formik.values.title_ar}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>

              <div className="mb-3">
                <Label for="description">{t("Description")}</Label>
                <Input
                  type="textarea"
                  id="description"
                  name="description"
                  rows="4"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>

              <div className="mb-3">
                <Label for="description_ar">{t("Description (AR)")}</Label>
                <Input
                  type="textarea"
                  id="description_ar"
                  name="description_ar"
                  rows="4"
                  value={formik.values.description_ar}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>

              <div className="mb-3">
                <Label for="imageFile">{t("Image")}</Label>
                <Input
                  type="file"
                  id="imageFile"
                  accept="image/*"
                  onChange={handleImageChange}
                />
              </div>

              <div className="mb-3">
                <Label for="site">{t("Select Site")}</Label>
                <select
                  id="site"
                  className="form-control"
                  value={selectedSiteId || ""}
                  onChange={(e) => {
                    const newSiteId = e.target.value ? parseInt(e.target.value) : null;
                    setSelectedSiteId(newSiteId);
                    // Clear selected spaces when site changes (will be auto-selected when spaces load)
                    setSelectedSpaceIds([]);
                  }}
                >
                  <option value="">{t("Select a site")}</option>
                  {sites?.map((site) => (
                    <option key={site.site_id} value={site.site_id}>
                      {site.site_id} - {site.name}
                    </option>
                  ))}
                </select>
              </div>

              {selectedSiteId && (
                <div className="mb-3">
                  <Label for="spaces">{t("Select Spaces")}</Label>
                  <Select
                    id="spaces"
                    isMulti
                    options={
                      isAllSelected
                        ? [{ value: "ALL", label: t("All Spaces") }]
                        : [
                            { value: "ALL", label: t("All Spaces") },
                            ...(allSpaces?.map((space) => ({
                              value: space.space_id,
                              label: `${space.space_id} - ${space.name}`,
                            })) || [])
                          ]
                    }
                    value={
                      isAllSelected
                        ? [{ value: "ALL", label: t("All") }]
                        : allSpaces
                            ?.filter((space) => actualSelectedSpaceIds.includes(space.space_id))
                            .map((space) => ({
                              value: space.space_id,
                              label: `${space.space_id} - ${space.name}`,
                            })) || []
                    }
                    onChange={(selectedOptions) => {
                      const selectedValues = selectedOptions
                        ? selectedOptions.map((option) => option.value)
                        : [];
                      
                      // Check if "All" was selected
                      if (selectedValues.includes("ALL")) {
                        // Select all spaces
                        setSelectedSpaceIds(["ALL"]);
                      } else {
                        // Check if all individual spaces are selected
                        const allSpaceIds = allSpaces.map((space) => space.space_id);
                        const allSelected = allSpaceIds.length > 0 && 
                          allSpaceIds.every((id) => selectedValues.includes(id));
                        
                        if (allSelected) {
                          // Automatically select "All" if all spaces are selected
                          setSelectedSpaceIds(["ALL"]);
                        } else {
                          // Set individual space selections
                          setSelectedSpaceIds(selectedValues);
                        }
                      }
                    }}
                    className="basic-multi-select"
                    classNamePrefix="select"
                    placeholder={t("Select one or more spaces...")}
                    isClearable
                    isSearchable
                  />
                  {actualSelectedSpaceIds.length > 0 && !isAllSelected && (
                    <small className="text-muted d-block mt-1">
                      {t("You can select multiple spaces by clicking on them")}
                    </small>
                  )}
                </div>
              )}

              <div className="mb-3">
                <Label for="starts_at">{t("Starts At")}</Label>
                <Input
                  type="datetime-local"
                  id="starts_at"
                  name="starts_at"
                  value={formik.values.starts_at}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>

              <div className="mb-3">
                <Label for="expires_at">{t("Expires At")}</Label>
                <Input
                  type="datetime-local"
                  id="expires_at"
                  name="expires_at"
                  value={formik.values.expires_at}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>

              <div className="mb-3">
                <FormControlLabel
                  control={
                    <Switch
                      checked={formik.values.see_once}
                      onChange={(e) =>
                        formik.setFieldValue("see_once", e.target.checked)
                      }
                />
                  }
                  label={t("See Once")}
                />
              </div>

              <div className="d-flex gap-2 mt-4">
                <Button type="submit" color="success" disabled={isLoading}>
                  {isLoading ? <Spinner size="sm" /> : t("Add")}
                </Button>
                <Button
                  color="danger"
                  onClick={() => {
                    toggleAddAnnouncementModal();
                    formik.resetForm();
                    setImagePreview(null);
                    setImageFile(null);
                  }}
                  disabled={isLoading}
                >
                  {t("Cancel")}
                </Button>
              </div>
            </form>
          </Col>

          <Col md={6}>
            <div className="sticky-top" style={{ top: "20px" }}>
              <Label className="mb-2">
                <strong>{t("Preview")}</strong>
              </Label>
              <div className="mb-2">
                <Button
                  size="sm"
                  outline
                  color={previewLanguage === "EN" ? "primary" : "secondary"}
                  onClick={() => setPreviewLanguage("EN")}
                  className="me-2"
                >
                  EN
                </Button>
                <Button
                  size="sm"
                  outline
                  color={previewLanguage === "AR" ? "primary" : "secondary"}
                  onClick={() => setPreviewLanguage("AR")}
                >
                  AR
                </Button>
              </div>

              {/* Mobile Preview */}
              <div
                style={{
                  maxWidth: "375px",
                  margin: "0 auto",
                  position: "relative",
                  backgroundColor: "#2a2a2a", // Dark grey background
                  borderRadius: "16px",
                  padding: "20px",
                  minHeight: "500px",
                }}
              >
                {/* White Popup Card */}
                <Card
                  style={{
                    backgroundColor: "#ffffff",
                    borderRadius: "16px",
                    overflow: "hidden",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
                    position: "relative",
                  }}
                >
                  {/* Close Button - Top Right */}
                  <button
                    onClick={() => {}}
                    style={{
                      position: "absolute",
                      top: "12px",
                      right: "12px",
                      width: "32px",
                      height: "32px",
                      borderRadius: "4px",
                      border: "1px solid #000",
                      backgroundColor: "transparent",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 10,
                      padding: 0,
                    }}
                  >
                    <span
                      style={{
                        fontSize: "18px",
                        fontWeight: "bold",
                        color: "#000",
                        lineHeight: "1",
                      }}
                    >
                      ×
                    </span>
                  </button>

                  <CardBody style={{ padding: "24px 20px 20px 20px" }}>
                    {/* Centered Header Text */}
                    <div
                      style={{
                        textAlign: "center",
                        marginBottom: "20px",
                        direction: previewLanguage === "AR" ? "rtl" : "ltr",
                      }}
                    >
                      <h5
                        style={{
                          marginBottom: "8px",
                          fontSize: "22px",
                          fontWeight: "700",
                          color: "#000",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        {previewData?.title}
                      </h5>
                      <p
                        style={{
                          marginBottom: 0,
                          fontSize: "16px",
                          color: "#000",
                          fontWeight: "400",
                        }}
                      >
                        {previewData?.description.split('\n')[0] }
                      </p>
                    </div>

                    {/* Large Image */}
                    <div
                      style={{
                        position: "relative",
                        width: "100%",
                        marginBottom: "20px",
                        borderRadius: "8px",
                        overflow: "hidden",
                        backgroundColor: "#f0f0f0",
                        minHeight: "250px",
                      }}
                    >
                      {imagePreview ? (
                        <img
                          src={imagePreview}
                          alt="Announcement"
                          style={{
                            width: "100%",
                            height: "250px",
                            display: "block",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            color: "#999",
                            fontSize: "14px",
                          }}
                        >
                          {t("No Image")}
                        </div>
                      )}
                    </div>

                    
                  </CardBody>
                </Card>
              </div>
            </div>
          </Col>
        </Row>
      </ModalBody>
    </Modal>
  );
};

export default AddAnnouncement;

